import React, { useState } from 'react';
import { Button, Input } from 'lift-ui';
import validator from 'validator';

import { sendPasswordResetEmail } from 'firebase/auth';
import { authFirebase } from '@lift-admin/utils';
import { Toastify } from 'lift-ui';
import { LoginAnimatedBackground } from '../ui/LoginAnimatedBackground';

interface ForgotPasswordProps {
  changeUserAction: (agr: string) => void;
  changeEmail: (agr: string) => void;
}

const ForgotPassword = ({
  changeUserAction,
  changeEmail,
}: ForgotPasswordProps) => {
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [showLoading, setShowLoading] = useState(false);

  const forgotPassword = async (email: string) => {
    try {
      await sendPasswordResetEmail(authFirebase, email);
      Toastify('success', `Password reset sent to email ${email}`);
    } catch (error) {
      Toastify('error', 'Error sending password reset email');
      console.error('Error sending password reset email', error);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!email) {
        setEmailError('Email is required');
      } else if (!validator.isEmail(email)) {
        setEmailError('Please enter a valid email');
      } else {
        setEmailError('');
        setShowLoading(true);
        await forgotPassword(email);
        setShowLoading(false);
        changeUserAction('login');
        changeEmail(email);
      }
    } catch (err: any) {
      setShowLoading(false);
      setEmailError(err.response?.data?.message || 'An error occurred');
    }
  };

  return (
    <div className="min-h-screen flex min-w-full">
      {/* Left side - Form */}
      <div className="w-full md:w-1/2 bg-white flex flex-col justify-center items-center p-0 min-h-full relative">
        {/* Logo at top-left */}
        <button
          type="button"
          className="absolute top-6 left-8"
          onClick={() => changeUserAction('login')}
          aria-label="Go to login"
        >
          <img
            src="https://storage.googleapis.com/ltp-static-data/lift_assets/platform-logo.svg"
            alt="LIFT"
            className="h-6 w-auto"
          />
        </button>

        {/* Centered form */}
        <div className="max-w-md w-full px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">RESET PASSWORD</h1>
            <p className="text-gray-600">Enter your email to receive a password reset link</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder="jdoe@newengen.com"
                disabled={showLoading}
                className={emailError ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              {emailError && <p className="text-red-600 text-sm mt-1">{emailError}</p>}
            </div>

            {/* Submit Button */}
            <Button type="submit" variant="default" className="w-full" disabled={showLoading}>
              {showLoading ? 'Sending Reset Link...' : 'Send Reset Link'}
            </Button>
          </form>

          {/* Back to Login Link */}
          <div className="mt-6 text-center">
            <span className="text-gray-600">Remember your password? </span>
            <button
              type="button"
              onClick={() => changeUserAction('login')}
              className="text-gray-900 font-medium hover:underline"
            >
              Log In
            </button>
          </div>
        </div>
      </div>

      {/* Right side - Animated Background */}
      <LoginAnimatedBackground />
    </div>
  );
};

export default ForgotPassword;
